import React, { Fragment, useEffect, useRef, useState } from "react";
import { Message } from "./ChatWindow";
import MessageBoxLoading from "../Message/MessageBoxLoading";
import MessageInput from "../Message/MessageInput";
import EarlierFilesBanner from "../KnowledgeBase/EarlierFilesBanner";
import MessageBox from "../Message/MessageBox";
import ScrollToBottomButton from "./ScrollToBottomButton";
import mermaid from "mermaid";
import FloatingAudioPlayer from "../VoiceMode/FloatingAudioPlayer";
import { useTheme } from "../../context/ThemeContext";

const Chat = ({
  loading,
  analyzing,
  browsing,
  messages,
  sendMessage,
  handleMic,
  isRecording,
  isSpeaking,
  messageAppeared,
  rewrite,
  stopMessageStream,
  createVirtualAssistant,
  clientLogoUrl,
  logoLoading,
  logoError,
  fullConversationAudioUrl,
  quotedText,
  sourceMessageId,
  handleQuote,
  clearQuote,
}: {
  messages: Message[];
  sendMessage: (message: string, files?: File[], connector?: string, quotedText?: string, quotedMessageId?: string) => void;
  handleMic: () => void;
  isRecording: boolean;
  isSpeaking: boolean;
  loading: boolean;
  analyzing: boolean;
  browsing: boolean;
  messageAppeared: boolean;
  rewrite: (message_id: string) => void;
  stopMessageStream: () => void;
  createVirtualAssistant: () => void;
  clientLogoUrl: string | null;
  logoLoading: boolean;
  logoError: boolean;
  fullConversationAudioUrl: string | null;
  quotedText: string | null;
  sourceMessageId: string | null;
  handleQuote: (text: string, messageId: string) => void;
  clearQuote: () => void;
}) => {
  const [dividerWidth, setDividerWidth] = useState(0);
  const dividerRef = useRef<HTMLDivElement | null>(null);
  const messageEnd = useRef<HTMLDivElement | null>(null);
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);
  const lastUserScrollPosition = useRef(0);
  const [userJustSentMessage, setUserJustSentMessage] = useState(false);
  const lastMessageCount = useRef(0);
  const lastUserMessageRef = useRef<HTMLDivElement | null>(null);
  const chatContainerRef = useRef<HTMLDivElement | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isFilePreviewOpen, setIsFilePreviewOpen] = useState(false);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [highlightedQuote, setHighlightedQuote] = useState<{ messageId: string; text: string } | null>(null);
  const { resolvedTheme } = useTheme();

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (!chatContainerRef.current) return;

      const container = chatContainerRef.current;
      const scrolledPosition = container.scrollTop + container.clientHeight;
      const bottomPosition = container.scrollHeight;

      if (scrolledPosition < bottomPosition - 100) {
        setShouldAutoScroll(false);
        setUserJustSentMessage(false);
      }

      if (scrolledPosition >= bottomPosition - 10) {
        setShouldAutoScroll(true);
        setShowScrollButton(false);
      }

      lastUserScrollPosition.current = scrolledPosition;
    };

    const container = chatContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, []);

  useEffect(() => {
    if (messages.length > lastMessageCount.current) {
      const newMessageIndex = messages.length - 1;
      const newMessage = messages[newMessageIndex];

      if (newMessage.role === 'user') {
        setUserJustSentMessage(true);

        setTimeout(() => {
          if (lastUserMessageRef.current && chatContainerRef.current) {
            const container = chatContainerRef.current;
            const messageRect = lastUserMessageRef.current.getBoundingClientRect();
            const containerRect = container.getBoundingClientRect();
            const scrollTop = messageRect.top - containerRect.top + container.scrollTop;

            container.scrollTo({
              top: scrollTop,
              behavior: "smooth"
            });
          }
        }, 100);
      }
    }

    // Handle scroll button visibility and auto-scrolling
    if (!shouldAutoScroll && messages.length > 0) {
      setShowScrollButton(true);
    } else if (shouldAutoScroll) {
      if (chatContainerRef.current) {
        chatContainerRef.current.scrollTo({
          top: chatContainerRef.current.scrollHeight,
          behavior: "smooth"
        });
      }
    }

    lastMessageCount.current = messages.length;

    if (messages.length === 1) {
      document.title = `${messages[0].content.parts.substring(
        0,
        30
      )} - Kyva`;
    }
  }, [messages, shouldAutoScroll, userJustSentMessage]);

  useEffect(() => {
    const updateDividerWidth = () => {
      if (dividerRef.current) {
        setDividerWidth(dividerRef.current.scrollWidth);
      }
    };
    updateDividerWidth();
    window.addEventListener("resize", updateDividerWidth);
    return () => {
      window.removeEventListener("resize", updateDividerWidth);
    };
  }, []);

  const handleSendMessage = (message: string, files: File[] = []) => {
    // Pass quotedText as the 4th argument (after connector) and sourceMessageId as 5th
    sendMessage(message, files, undefined, quotedText || undefined, sourceMessageId || undefined);
    setUserJustSentMessage(true);
    if (quotedText) clearQuote(); // Clear quote after sending
  };

  // ADD THIS NEW FUNCTION
  const handleFilePreviewStateChange = (isOpen: boolean) => {
    setIsFilePreviewOpen(isOpen);
  };

  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTo({
        top: chatContainerRef.current.scrollHeight,
        behavior: "smooth"
      });
      setShouldAutoScroll(true);
      setShowScrollButton(false);
    }
  };

  useEffect(() => {
    mermaid.initialize({
      startOnLoad: false,
      suppressErrorRendering: true,
      theme: resolvedTheme === "dark" ? "dark" : "default",
    });
  }, [resolvedTheme]);

  // Dynamic styles based on screen size
  const getMessagesAreaStyles = () => {
    const baseStyles = {
      position: "fixed" as const,
      top: "10px",
      bottom: "120px",
      overflow: "auto" as const,
      overflowX: "hidden" as const,
      zIndex: 1
    };

    if (isMobile) {
      // Mobile: Move content left
      return {
        ...baseStyles,
        left: "10px", // Reduced from sidebar padding
        right: "10px",
        paddingLeft: "12px",
        paddingRight: "12px",
      };
    } else {
      // Desktop: Move content more to the right
      return {
        ...baseStyles,
        left: "0",
        right: "0",
        paddingLeft: "calc(24px + 15%)", // Increased from 10% to 15%
        paddingRight: "24px",
      };
    }
  };

  const getMessageInputStyles = () => {
    const baseStyles = {
      position: "fixed" as const,
      bottom: "40px",
      zIndex: 40,
      pointerEvents: "auto" as const
    };

    if (isMobile) {
      return {
        ...baseStyles,
        left: "20px",
        right: "20px",
      };
    } else {
      return {
        ...baseStyles,
        left: "calc(44px + 15%)",
        right: "44px",
      };
    }
  };

  return (
    <>
      {/* Chat Messages Area with responsive positioning and purple scrollbar */}
      <div
        ref={chatContainerRef}
        className="chat-messages-area custom-scrollbar"
        style={getMessagesAreaStyles()}
      >
        <div
          className="flex flex-col space-y-6 mx-auto w-full max-w-3xl"
          style={{
            paddingTop: "2rem",
            paddingBottom: "2rem",
            paddingLeft: isMobile ? "12px" : "24px",
            paddingRight: isMobile ? "12px" : "24px",
            minHeight: "100%"
          }}
        >
          {messages.map((msg, i) => {
            const isLast = i === messages.length - 1;
            const isUserMessage = msg.role === 'user';
            const lastUserMessageIndex = messages.map(m => m.role === 'user').lastIndexOf(true);
            const isLastUserMessage = isUserMessage && i === lastUserMessageIndex;

            return (
              <Fragment key={msg.id}>
                <div ref={isLastUserMessage ? lastUserMessageRef : undefined}>
                  <MessageBox
                    message={msg}
                    messageIndex={i}
                    history={messages}
                    loading={loading}
                    dividerRef={isLast ? dividerRef : undefined}
                    isLast={isLast}
                    rewrite={rewrite}
                    sendMessage={handleSendMessage}
                    createVirtualAssistant={createVirtualAssistant}
                    clientLogoUrl={clientLogoUrl}
                    logoLoading={logoLoading}
                    logoError={logoError}
                    onFilePreviewStateChange={handleFilePreviewStateChange}
                    handleQuote={handleQuote}
                    highlightedQuote={highlightedQuote}
                    setHighlightedQuote={setHighlightedQuote}
                  />
                </div>
              </Fragment>
            );
          })}

          {(loading || analyzing || browsing) && !messageAppeared && (
            <MessageBoxLoading analyzing={analyzing} browsing={browsing} />
          )}

          <div ref={messageEnd} className="h-0" />
        </div>
      </div>

      {/* Message Input Area with responsive positioning */}
      <div
        className="message-input-area"
        style={getMessageInputStyles()}
      >
        <div className="w-full max-w-3xl mx-auto relative">
          {!isFilePreviewOpen && <EarlierFilesBanner messages={messages} />}
          <MessageInput
            loading={loading}
            sendMessage={handleSendMessage}
            stopMessageStream={stopMessageStream}
            hidden={isFilePreviewOpen}
            quotedText={quotedText}
            clearQuote={clearQuote}
          />
          {/* Floating Audio Player */}
          {fullConversationAudioUrl && (
            <FloatingAudioPlayer audioUrl={fullConversationAudioUrl} />
          )}

          {/* Scroll to Bottom Button */}
          <ScrollToBottomButton
            onClick={scrollToBottom}
            visible={showScrollButton}
            className={
              fullConversationAudioUrl
                ? "absolute left-1/2 -translate-x-1/2 -top-28 z-20"
                : "fixed right-6 z-20"
            }
            style={
              !fullConversationAudioUrl
                ? { bottom: isMobile ? "140px" : "150px" }
                : undefined
            }
          />
        </div>
      </div>

    </>
  );
};

export default Chat;
